(() => {
  const CHANGE_EVENT = 'leobela:wishlist:change';
  const READY_EVENT = 'leobela:wishlist:ready';
  const CONCURRENCY = 4;

  const createPlaceholder = (item) => {
    const placeholder = document.createElement('li');
    placeholder.className = 'leobela-wishlist-page__placeholder';
    placeholder.dataset.wishlistPlaceholderId = item.productId;
    placeholder.setAttribute('aria-hidden', 'true');
    return placeholder;
  };

  const productUrl = (section, handle) =>
    section.dataset.productUrlTemplate.replace('__HANDLE__', encodeURIComponent(handle));

  const parseProductCard = (html, expectedProductId) => {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const card = documentFragment.querySelector('[data-wishlist-page-item]');

    if (!card || card.dataset.wishlistProductId !== expectedProductId) return null;
    return card;
  };

  const setEmptyState = (section, isEmpty) => {
    section.querySelector('[data-wishlist-page-empty]').hidden = !isEmpty;
    section.querySelector('[data-wishlist-page-grid]').hidden = isEmpty;

    const toolbar = section.querySelector('[data-wishlist-page-toolbar]');
    if (toolbar) toolbar.hidden = isEmpty;
  };

  const renderWishlist = async (section) => {
    const wishlist = window.LeobelaWishlist;
    if (!wishlist) return;

    const items = wishlist.getItems().slice(0, 100);
    const grid = section.querySelector('[data-wishlist-page-grid]');
    const status = section.querySelector('[data-wishlist-page-status]');
    const error = section.querySelector('[data-wishlist-page-error]');
    const renderId = String(Date.now());

    section.dataset.wishlistRenderId = renderId;
    grid.replaceChildren(...items.map(createPlaceholder));
    grid.setAttribute('aria-busy', 'true');
    error.hidden = true;

    if (items.length === 0) {
      grid.setAttribute('aria-busy', 'false');
      status.hidden = true;
      setEmptyState(section, true);
      return;
    }

    status.hidden = false;
    setEmptyState(section, false);

    let nextIndex = 0;
    let failedCount = 0;

    const loadItem = async (item) => {
      const placeholder = grid.querySelector(
        `[data-wishlist-placeholder-id="${CSS.escape(item.productId)}"]`
      );
      if (!placeholder) return;

      try {
        const response = await fetch(productUrl(section, item.handle), {
          headers: { Accept: 'text/html' },
        });

        if (section.dataset.wishlistRenderId !== renderId) return;

        if (response.status === 404 || response.status === 410) {
          placeholder.remove();
          wishlist.remove(item.productId);
          return;
        }

        if (!response.ok) throw new Error(`Wishlist product request failed: ${response.status}`);

        const card = parseProductCard(await response.text(), item.productId);
        if (!card) throw new Error('Wishlist product response did not contain a valid card');

        placeholder.replaceWith(card);
      } catch (requestError) {
        placeholder.remove();
        failedCount += 1;
      }
    };

    const worker = async () => {
      while (nextIndex < items.length) {
        const item = items[nextIndex];
        nextIndex += 1;
        await loadItem(item);
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, worker));
    if (section.dataset.wishlistRenderId !== renderId) return;

    status.hidden = true;
    grid.setAttribute('aria-busy', 'false');
    const remainingItems = wishlist.getItems();

    if (remainingItems.length === 0) {
      setEmptyState(section, true);
    } else if (failedCount > 0) {
      error.hidden = false;
    }
  };

  const removeRenderedCard = (section, productId) => {
    const card = Array.from(section.querySelectorAll('[data-wishlist-page-item]')).find(
      (item) => item.dataset.wishlistProductId === productId
    );
    const placeholder = Array.from(section.querySelectorAll('[data-wishlist-placeholder-id]')).find(
      (item) => item.dataset.wishlistPlaceholderId === productId
    );

    placeholder?.remove();
    if (!card) return;

    const focusWasInside = card.contains(document.activeElement);
    const cards = Array.from(section.querySelectorAll('[data-wishlist-page-item]'));
    const cardIndex = cards.indexOf(card);
    const nextCard = cards[cardIndex + 1] || cards[cardIndex - 1];

    card.classList.add('is-removing');

    window.setTimeout(() => {
      card.remove();

      if (window.LeobelaWishlist.getItems().length === 0) {
        setEmptyState(section, true);
      }

      if (focusWasInside) {
        const focusTarget = nextCard?.querySelector('.wishlist-button__control')
          || section.querySelector('[data-wishlist-page-empty] .button');
        focusTarget?.focus();
      }
    }, 170);
  };

  const initialize = (root = document) => {
    root.querySelectorAll('[data-wishlist-page]').forEach((section) => renderWishlist(section));
  };

  let initialRenderComplete = false;

  const initializeWhenReady = () => {
    if (initialRenderComplete || !window.LeobelaWishlist) return;
    initialRenderComplete = true;
    initialize();
  };

  document.addEventListener(READY_EVENT, initializeWhenReady, { once: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWhenReady, { once: true });
  } else {
    initializeWhenReady();
  }

  document.addEventListener('click', (event) => {
    const clearButton = event.target.closest?.('[data-wishlist-clear]');

    if (clearButton) {
      const section = clearButton.closest('[data-wishlist-page]');
      const wishlist = window.LeobelaWishlist;
      if (!section || !wishlist || wishlist.getItems().length === 0) return;

      const dialog = section.querySelector('[data-wishlist-clear-dialog]');
      if (!dialog) return;

      dialog.showModal();
      dialog.querySelector('[data-wishlist-clear-cancel]')?.focus();
      return;
    }

    const cancelButton = event.target.closest?.('[data-wishlist-clear-cancel]');
    if (cancelButton) {
      cancelButton.closest('[data-wishlist-clear-dialog]')?.close();
      return;
    }

    const confirmButton = event.target.closest?.('[data-wishlist-clear-confirm]');
    if (!confirmButton) return;

    const dialog = confirmButton.closest('[data-wishlist-clear-dialog]');
    const wishlist = window.LeobelaWishlist;

    if (wishlist && wishlist.getItems().length > 0) {
      wishlist.clear();
    }

    dialog?.close();
  });

  document.addEventListener(CHANGE_EVENT, (event) => {
    document.querySelectorAll('[data-wishlist-page]').forEach((section) => {
      if (!event.detail?.productId) {
        renderWishlist(section);
      } else if (event.detail.isSaved === false) {
        removeRenderedCard(section, event.detail.productId);
      }
    });
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
